import { ReactElement } from "react";
import { Anchor } from "@twilio-paste/core/anchor";
import { Box } from "@twilio-paste/core/box";
import { Heading } from "@twilio-paste/core/heading";
import { Text } from "@twilio-paste/core/text";
import RouterAnchor from "@twiliointernal/services-common/components/routerAnchor";

import {
  OscPreChatHeaderProps,
  WiseOwlIngress,
} from "@twiliointernal/e2-commons-shared-definitions";
import {
  oneAdminPreChatConfiguration,
  wozdPreChatConfiguration,
  wosfPreChatConfiguration,
  deprecatedOscPreChatConfiguration,
} from "../../definitions";
import landingIllustration from "../../../../assets/landing-illustration.svg";
import { useWiseOwlContext } from "../../../../hooks";
import { CompositeCallout } from "@twiliointernal/e2-commons-shared-ui";
import styles from "./DeprecatedOscPreChatHeader.module.css";

const OscPreChatHeader = (): ReactElement => {
  const { applicationProps, isOneAdmin } = useWiseOwlContext();
  const { ingress } = applicationProps;

  const getPreChatConfiguration = (): OscPreChatHeaderProps => {
    // One Admin ingress is not supported yet
    if (isOneAdmin) {
      return oneAdminPreChatConfiguration;
    }

    switch (ingress) {
      case WiseOwlIngress.zendesk: {
        return wozdPreChatConfiguration;
      }
      case WiseOwlIngress.salesforce:
      case WiseOwlIngress.wiseOwlApp: {
        return wosfPreChatConfiguration;
      }
      // Default to OSC
      default: {
        return deprecatedOscPreChatConfiguration;
      }
    }
  };

  const renderPreChatHeader = ({
    hasBanner,
    hasImage,
    headerText,
    subText,
    anchorProps,
  }: OscPreChatHeaderProps): ReactElement => (
    <Box as="div">
      <Box as="div" display="flex" columnGap="space50" alignItems="center">
        {hasImage && (
          <Box
            as="img"
            src={landingIllustration}
            alt="chat box header"
            className={styles.preChatImage}
            height="12rem"
            width="12rem"
          />
        )}
        {/* Add or remove this CompositeCallout to change the banner on the OSC Wise Owl landing */}
        {/* Banner added: August 5, 2024 */}
        {/* Planned removal: September 3, 2024 */}
        {hasBanner && (
          <CompositeCallout
            element="OSC_BANNER"
            variant="new"
            marginBottom="space0"
            heading="UI updates have begun rolling out to our customer-facing Console
                and Help Center sites, with more on the way."
            description={
              <Box as="div">
                For full details on what is changing and when, please see our{" "}
                <RouterAnchor to="/articles/27885350503195-Twilio-Console-and-Help-Center-UI-Updates">
                  Support article
                </RouterAnchor>
                .
              </Box>
            }
          />
        )}
      </Box>
      <Heading as="h2" variant="heading20" marginBottom="space0">
        {headerText}
      </Heading>
      <Text as="p" display={"flex"}>
        {subText}

        {anchorProps && (
          <Anchor
            href={anchorProps.href}
            onClick={anchorProps.onClick}
            showExternal={anchorProps.showExternal}
            marginLeft="space20"
          >
            {anchorProps.children}
          </Anchor>
        )}
      </Text>
    </Box>
  );

  const preChatConfiguration = getPreChatConfiguration();

  return renderPreChatHeader(preChatConfiguration);
};

export default OscPreChatHeader;
